import AttendanceSidePanel from "./attendanceSidePanel";
import React, { useState, useMemo, useEffect, useRef } from "react";
import { Card, Col, Row, Button, Dropdown, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Background from "components/common/background";
import corner4 from "assets/img/illustrations/corner-4.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserCheck,
  faUmbrellaBeach,
  faBriefcaseMedical,
  faClock,
  faTimesCircle,
  faArrowLeft,
  faArrowRight,
  faFileExcel,
  faFilePdf,
  faFileCsv,
} from "@fortawesome/free-solid-svg-icons";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AdvanceTableSearchBox from "components/common/advance-table/advanceTableSearchBox";
import SubtleBadge from "components/common/subtleBadge";
import { toast } from "react-toastify";
import moment from "moment";
import axios from "axios";
import { writeFile, utils } from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import ManualPaginationFooter from "components/common/advance-table/manualPaginationFooter";
import AdvanceTableProvider from "providers/advanceTableProvider";
import useServerAdvanceTable from "hooks/useServerAdvanceTable";
import { getInitialPageSize, getUnicodePdfHooks, drawPdfText, toSafeFileName, toSafeSheetName, buildExportFileName } from "assets/js/common";
import { useAppContext } from "providers/appProvider";

const DATE_FORMAT = "YYYY-MM-DD";
const MAX_PAGE_SIZE = 100;

const formatDuration = (seconds, t) => {
  if (seconds === null || seconds === undefined) return null;
  const totalMinutes = Math.round(seconds / 60);
  return `${Math.floor(totalMinutes / 60)}${t("hourShort", "hr")}${String(totalMinutes % 60).padStart(2, "0")}`;
};

const WEEKDAY_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const MONTH_KEYS = [
  "january", "february", "march", "april", "may", "june",
  "july", "august", "september", "october", "november", "december",
];

const formatClockTime = (isoDateTime) => (isoDateTime ? moment(isoDateTime).format("HH:mm") : null);

const isRequestCancelled = (error) => axios.isCancel(error) || error?.name === "CanceledError";

const fetchOverviewPage = async ({ view, customerId, locationId, periodKey, page, pageSize, search, kpi, signal }) => {
  const response = await axios.get(`${process.env.REACT_APP_API_URL}/attendanceRoute/admin/${view}`, {
    params: {
      customerId,
      locationId,
      [view === "daily" ? "date" : "weekStart"]: periodKey,
      page,
      pageSize,
      search,
      kpi,
      tzOffset: moment().utcOffset(),
    },
    headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
    signal,
  });
  return response.data;
};

const fetchEmployeeAttendance = async ({ customerId, userId, startDate, endDate, signal }) => {
  const response = await axios.get(`${process.env.REACT_APP_API_URL}/attendanceRoute/admin/employee/${userId}`, {
    params: { customerId, startDate, endDate, tzOffset: moment().utcOffset() },
    headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
    signal,
  });
  return response.data;
};


const getColorFromName = (name = "") => {
  const colors = [
    "bg-primary-subtle text-primary",
    "bg-success-subtle text-success",
    "bg-warning-subtle text-warning",
    "bg-danger-subtle text-danger",
    "bg-info-subtle text-info",
  ];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
};

const STATUS_CONFIG = {
  W: { labelKey: "attendanceOverview.status.worked", label: "Worked", shortLabelKey: "attendanceOverview.status.worked", shortLabel: "Worked", badgeColor: "success", subtleClass: "bg-success-subtle text-success" },
  L: { labelKey: "attendanceOverview.status.leave", label: "Leave", shortLabelKey: "attendanceOverview.status.leave", shortLabel: "Leave", badgeColor: "info", subtleClass: "bg-info-subtle text-info" },
  S: { labelKey: "attendanceOverview.status.sick", label: "Sick", shortLabelKey: "attendanceOverview.status.sick", shortLabel: "Sick", badgeColor: "pink", subtleClass: "bg-pink-subtle text-pink" },
  A: { labelKey: "attendanceOverview.status.absent", label: "Absent", shortLabelKey: "attendanceOverview.status.absent", shortLabel: "Absent", badgeColor: "danger", subtleClass: "bg-danger-subtle text-danger" },
  M: { labelKey: "attendanceOverview.status.missing", label: "Missing reg.", shortLabelKey: "attendanceOverview.status.missingShort", shortLabel: "Missing", badgeColor: "warning", dashed: true, subtleClass: "bg-warning-subtle text-warning" },
  P: { labelKey: "attendanceOverview.status.pending", label: "Pending", shortLabelKey: "attendanceOverview.status.pending", shortLabel: "Pending", badgeColor: "amber", subtleClass: "bg-amber-subtle text-amber" },
  N: { labelKey: "attendanceOverview.status.notScheduled", label: "Not sched.", shortLabelKey: "attendanceOverview.status.off", shortLabel: "Off", badgeColor: "secondary", subtleClass: "bg-secondary-subtle text-secondary" },
  H: { labelKey: "attendanceOverview.status.holiday", label: "Holiday", shortLabelKey: "attendanceOverview.status.holiday", shortLabel: "Holiday", badgeColor: "purple", subtleClass: "bg-purple-subtle text-purple" },
};

const API_STATUS_TO_CODE = { worked: "W", leave: "L", sick: "S", absent: "A", missing: "M", pending: "P", notScheduled: "N", holiday: "H" };

const getInitials = (name = "") => {
  const words = name
    .replace(/\(.*?\)/g, "")     
    .replace(/[^\p{L}\p{N}\s]/gu, "") 
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) return "";
  if (words.length === 1) return words[0][0].toUpperCase();

  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

const EMPTY_SUMMARY = { working: 0, onLeave: 0, sick: 0, notRegistered: 0, absent: 0 };
const KPI_LABELS = {
  working: "attendanceOverview.workingLabel",
  onLeave: "attendanceOverview.onLeaveLabel",
  sick: "attendanceOverview.sickLabel",
  notRegistered: "attendanceOverview.notRegisteredLabel",
  absent: "attendanceOverview.absentLabel",
};
const DATE_SHIFT_DAYS = { daily: 1, weekly: 7 };
const HISTORY_DAYS = 7; 

const toDayData = (cell, t) => ({
  s: API_STATUS_TO_CODE[cell.status] || "N",
  isFuture: !cell.status,
  checkIn: formatClockTime(cell.checkIn),
  checkOut: formatClockTime(cell.checkOut),
  formattedHours: cell.isLive ? null : formatDuration(cell.workedSeconds, t),
  formattedBreak: cell.checkIn ? formatDuration(cell.breakSeconds, t) : null,
  isLive: !!cell.isLive,
});

const toEmployee = (row) => ({
  ...row,
  id: row.userId,
  name: row.name || "",
  initials: getInitials(row.name || ""),
  avatarClass: getColorFromName(row.name || ""),
});

const Attendance = () => {
  const { t } = useTranslation();
  const { config } = useAppContext();
  const customerId = localStorage.getItem("customerId");

  const [activeView, setActiveView] = useState("daily");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeKpiFilter, setActiveKpiFilter] = useState("all");

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(getInitialPageSize("attendanceTable"));
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  const [dailyData, setDailyData] = useState(null);
  const [weeklyData, setWeeklyData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const [selectedDrawerEmployee, setSelectedDrawerEmployee] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerDate, setDrawerDate] = useState(null);
  const [drawerHistory, setDrawerHistory] = useState([]);
  const [isDrawerHistoryLoading, setIsDrawerHistoryLoading] = useState(false);
  const [hasDrawerHistoryError, setHasDrawerHistoryError] = useState(false);
  const drawerRequestRef = useRef(0);

  const handleDateShift = (direction) => {
    const shiftDays = DATE_SHIFT_DAYS[activeView];
    setSelectedDate((prevDate) => moment(prevDate).add(direction * shiftDays, "days").toDate());
  };

  const weekStart = moment(selectedDate).startOf("isoWeek");
  const weekEnd = moment(selectedDate).endOf("isoWeek");

  const formatShortDate = (date, withYear = false) =>
    `${date.date()} ${t(`shortMonths.${MONTH_KEYS[date.month()]}`, date.format("MMM"))}${withYear ? ` ${date.year()}` : ""}`;

  const dateDisplayValue =
    activeView === "weekly"
      ? `${formatShortDate(weekStart)} - ${formatShortDate(weekEnd, true)}`
      : moment(selectedDate).format("DD-MM-YYYY");

  const selectedDay = moment(selectedDate).format(DATE_FORMAT);
  const weekStartDay = weekStart.format(DATE_FORMAT);
  const periodKey = activeView === "daily" ? selectedDay : weekStartDay;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPageIndex(0);
  }, [activeView, periodKey, debouncedSearch, activeKpiFilter, config.location]);

  useEffect(() => {
    const controller = new AbortController();

    const loadOverview = async () => {
      setIsLoading(true);
      setHasLoadError(false);
      try {
        const data = await fetchOverviewPage({
          view: activeView,
          customerId,
          locationId: localStorage.getItem("LocationId"),
          periodKey,
          page: pageIndex + 1,
          pageSize,
          search: debouncedSearch,
          kpi: activeKpiFilter,
          signal: controller.signal,
        });

        if (activeView === "daily") setDailyData(data);
        else setWeeklyData(data);
        setTotalPages(data.totalPages || 0);
        setTotalCount(data.totalCount || 0);
        setIsLoading(false);
      } catch (error) {
        if (isRequestCancelled(error)) return;
        console.error("Error fetching attendance overview:", error);
        setHasLoadError(true);
        setIsLoading(false);
      }
    };

    loadOverview();
    return () => controller.abort();
  }, [activeView, periodKey, customerId, reloadKey, pageIndex, pageSize, debouncedSearch, activeKpiFilter, config.location]);

  const dailyRows = useMemo(
    () =>
      (dailyData?.rows || []).map((row) => {
        const dayData = toDayData(row, t);
        return { ...toEmployee(row), dayData, statusMeta: STATUS_CONFIG[dayData.s] };
      }),
    [dailyData, t]
  );

  const weekDays = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const m = moment(weekStartDay).add(i, "days");
        return {
          dateStr: m.format(DATE_FORMAT),
          dayName: t(`shortDays.${WEEKDAY_KEYS[m.isoWeekday() - 1]}`, m.format("ddd")),
          dayMonth: m.format("DD-MM"),
        };
      }),
    [weekStartDay, t]
  );

  const weeklyRows = useMemo(
    () =>
      (weeklyData?.rows || []).map((row) => ({
        ...toEmployee(row),
        days: row.days.map((day) => {
          const dayData = toDayData(day, t);
          return { ...day, dateStr: day.date, dayData, statusMeta: STATUS_CONFIG[dayData.s] };
        }),
      })),
    [weeklyData, t]
  );

  const attendanceTable = useServerAdvanceTable({
    data: activeView === "daily" ? dailyRows : weeklyRows,
    columns: [{ accessorKey: "name" }],
    selection: false,
    sortable: false,
    pagination: true,
    manualPagination: true,
    pageCount: totalPages,
    paginationState: { pageIndex, pageSize },
    onPaginationChange: (updater) => {
      const next = typeof updater === "function" ? updater({ pageIndex, pageSize }) : updater;
      setPageIndex(next.pageIndex);
      setPageSize(next.pageSize);
    },
  });

  const activeKpiCounts = (activeView === "daily" ? dailyData?.summary : weeklyData?.summary) || EMPTY_SUMMARY;

  const periodSubtext = t("attendanceOverview.employeeDaysWeek", "Employee-days this week");

  const handleRowClick = async (emp, clickedDate) => {
    const targetDay = clickedDate || selectedDay;
    const dayData =
      emp.dayData || emp.days?.find((d) => d.dateStr === targetDay)?.dayData || toDayData({}, t);
    const requestId = ++drawerRequestRef.current;

    setSelectedDrawerEmployee({
      ...emp,
      dayData,
      statusMeta: STATUS_CONFIG[dayData.s] || STATUS_CONFIG.N,
    });
    setDrawerDate(targetDay);
    setDrawerHistory([]);
    setHasDrawerHistoryError(false);
    setIsDrawerHistoryLoading(true);
    setIsDrawerOpen(true);

    try {
      const data = await fetchEmployeeAttendance({
        customerId,
        userId: emp.id,
        startDate: moment(targetDay).subtract(HISTORY_DAYS - 1, "days").format(DATE_FORMAT),
        endDate: targetDay,
      });
      if (requestId !== drawerRequestRef.current) return;

      setSelectedDrawerEmployee((prev) =>
        prev ? { ...prev, role: data.user?.role || prev.role, locations: data.user?.locations || [] } : prev
      );
      setDrawerHistory(
        (data.days || []).map((day) => {
          const history = toDayData(day, t);
          return {
            date: day.date,
            dayData: history,
            statusMeta: STATUS_CONFIG[history.s] || STATUS_CONFIG.N,
            place: day.location || null,
          };
        })
      );
    } catch (error) {
      if (requestId !== drawerRequestRef.current) return;
      console.error("Error fetching employee attendance:", error);
      setHasDrawerHistoryError(true);
    } finally {
      if (requestId === drawerRequestRef.current) setIsDrawerHistoryLoading(false);
    }
  };

  const statusLabel = (apiStatus) => {
    const meta = STATUS_CONFIG[API_STATUS_TO_CODE[apiStatus]];
    return meta ? t(meta.labelKey, meta.label) : "";
  };

  const buildExportTable = (rows) => {
    const inProgress = t("attendanceOverview.inProgress", "In progress");

    if (activeView === "daily") {
      return {
        title: `${t("menu.attendance", "Attendance")} - ${t("attendanceOverview.daily", "Daily")} (${moment(selectedDay).format("DD-MM-YYYY")})`,
        fileName: toSafeFileName(
          `${t("menu.attendance", "Attendance")}-${t("attendanceOverview.daily", "Daily")}-${selectedDay}`
        ),
        orientation: "portrait",
        header: [
          t("employee", "Employee"),
          t("status", "Status"),
          t("checkIn", "Check-in"),
          t("checkOut", "Check-out"),
          t("registeredHours", "Registered hours"),
        ],
        body: rows.map((row) => [
          row.name || "",
          statusLabel(row.status) || statusLabel("notScheduled"),
          formatClockTime(row.checkIn) || "",
          formatClockTime(row.checkOut) || "",
          row.isLive ? inProgress : formatDuration(row.workedSeconds, t) || "",
        ]),
      };
    }

    return {
      title: `${t("menu.attendance", "Attendance")} - ${t("attendanceOverview.weekly", "Weekly")} (${formatShortDate(weekStart)} - ${formatShortDate(weekEnd, true)})`,
      fileName: buildExportFileName({
        label: `${t("menu.attendance", "Attendance")}-${t("attendanceOverview.weekly", "Weekly")}`,
        from: weekStart,
        to: weekEnd,
      }),
      orientation: "landscape",
      header: [
        t("employee", "Employee"),
        ...weekDays.map((day) => `${day.dayName} ${day.dayMonth}`),
        t("attendanceOverview.totalHours", "Total hours"),
      ],
      body: rows.map((row) => [
        row.name || "",
        ...row.days.map((day) => {
          if (!day.status) return "";
          const hours = formatDuration(day.workedSeconds, t);
          return (day.isLive ? inProgress : statusLabel(day.status)) + (hours ? ` (${hours})` : "");
        }),
        formatDuration(row.totalWorkedSeconds, t) || "",
      ]),
    };
  };

  const exportToCsv = ({ fileName, header, body }) => {
    const escapeCell = (value) => {
      const text = String(value ?? "");
      const safeText = /^[\t\r ]*[=+\-@]/.test(text) ? `'${text}` : text;
      return `"${safeText.replace(/"/g, '""')}"`;
    };
    const csv = [header, ...body].map((row) => row.map(escapeCell).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });

    const downloadUrl = URL.createObjectURL(blob);
    const downloadLink = document.createElement("a");
    downloadLink.href = downloadUrl;
    downloadLink.download = `${fileName}.csv`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(downloadUrl);
  };

  const exportToExcel = ({ fileName, header, body }) => {
    const worksheet = utils.aoa_to_sheet([header, ...body]);
    worksheet["!cols"] = header.map((label, index) => ({
      wch: Math.max(String(label).length, ...body.map((row) => String(row[index] ?? "").length)) + 2,
    }));

    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, toSafeSheetName(t("menu.attendance", "Attendance"), "Attendance"));
    writeFile(workbook, `${fileName}.xlsx`);
  };

  const exportToPdf = ({ title, fileName, orientation, header, body }) => {
    const doc = new jsPDF({ orientation, unit: "pt", format: "a4" });
    drawPdfText(doc, title, 40, 40, 14);

    autoTable(doc, {
      head: [header],
      body: body.map((row) => row.map((cell) => (cell === "" ? "-" : cell))),
      startY: 55,
      styles: { fontSize: 8, cellPadding: 4 },
      headStyles: { fillColor: [44, 123, 229] },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      margin: { left: 40, right: 40 },
      ...getUnicodePdfHooks(doc),
    });

    doc.save(`${fileName}.pdf`);
  };

  const handleExport = async (format) => {
    if (isExporting) return;
    setIsExporting(true);

    try {
      const rows = [];
      let page = 1;
      let pagesInTotal = 1;
      do {
        const data = await fetchOverviewPage({
          view: activeView,
          customerId,
          locationId: localStorage.getItem("LocationId"),
          periodKey,
          page,
          pageSize: MAX_PAGE_SIZE,
          search: debouncedSearch,
          kpi: activeKpiFilter,
        });
        rows.push(...(data.rows || []));
        pagesInTotal = data.totalPages || 0;
        page += 1;
      } while (page <= pagesInTotal);

      const table = buildExportTable(rows);

      if (table.body.length === 0) {
        toast.info(t("noDataAvailable", "No data available"), { position: "top-right" });
        return;
      }

      if (format === "excel") exportToExcel(table);
      else if (format === "csv") exportToCsv(table);
      else exportToPdf(table);

      toast.success(t("attendanceOverview.exportSuccess", "Export completed"), { position: "top-right", autoClose: 2500 });
    } catch (error) {
      console.error("Error exporting attendance:", error);
      toast.error(t("somethingWentWrong", "Something went wrong"), { position: "top-right" });
    } finally {
      setIsExporting(false);
    }
  };

  const stateBlock = isLoading ? (
    <div className="text-center py-5">
      <Spinner animation="border" size="sm" />
    </div>
  ) : hasLoadError ? (
    <div className="text-center py-5 text-muted">
      <p className="mb-2">{t("attendanceOverview.loadError", "Could not load attendance. Please try again.")}</p>
      <Button variant="falcon-default" size="sm" onClick={() => setReloadKey((key) => key + 1)}>
        {t("attendanceOverview.retry", "Retry")}
      </Button>
    </div>
  ) : null;

  const kpiTiles = [
    {
      key: "working",
      label: t("attendanceOverview.workingLabel", "Working"),
      color: "success",
      badgeColor: "success",
      icon: faUserCheck,
      count: activeKpiCounts.working,
      actionLabel: `${t("view", "View")} →`,
      subtext: t("attendanceOverview.workingSubtext", "Present & logged hours"),
    },
    {
      key: "onLeave",
      label: t("attendanceOverview.onLeaveLabel", "On leave"),
      color: "warning",
      badgeColor: "warning",
      icon: faUmbrellaBeach,
      count: activeKpiCounts.onLeave,
      actionLabel: `${t("view", "View")} →`,
      subtext: t("attendanceOverview.onLeaveSubtext", "Approved absence"),
    },
    {
      key: "sick",
      label: t("attendanceOverview.sickLabel", "Sick"),
      color: "danger",
      badgeColor: "danger",
      icon: faBriefcaseMedical,
      count: activeKpiCounts.sick,
      actionLabel: `${t("view", "View")} →`,
      subtext: t("attendanceOverview.sickSubtext", "Reported sickness"),
    },
    {
      key: "notRegistered",
      label: t("attendanceOverview.notRegisteredLabel", "Not yet registered"),
      color: "primary",
      badgeColor: "primary",
      icon: faClock,
      count: activeKpiCounts.notRegistered,
      actionLabel: `${t("view", "View")} →`,
      subtext: t("attendanceOverview.notRegisteredSubtext", "Pending check-in"),
    },
    {
      key: "absent",
      label: t("attendanceOverview.absentLabel", "Absent"),
      color: "danger",
      badgeColor: "danger",
      icon: faTimesCircle,
      count: activeKpiCounts.absent,
      actionLabel: `${t("view", "View")} →`,
      subtext: t("attendanceOverview.absentSubtext", "Unexcused no-show"),
    },
  ];

  return (
    <>
      <Row className="g-3 mb-3">
        <Col lg={12} xxl={12}>
          <Card className="mb-3 h-100">
            <Background image={corner4} className="bg-card" />
            <Card.Body className="position-relative pe-3 pb-2">
              <Row className="flex-between-center">
                <Col>
                  <h5 className="mb-2 mb-md-0">{t("menu.attendance", "Attendance")}</h5>
                </Col>

                <Col xs="auto" className="d-flex align-items-center">
                  <div className="toggle-group d-inline-flex border rounded-3 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => {
                        setActiveView("daily");
                        setActiveKpiFilter("all");
                      }}
                      className={`border-0 px-3 py-1 ${activeView === "daily" ? "active" : ""}`}
                    >
                      {t("attendanceOverview.daily", "Daily")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveView("weekly");
                        setActiveKpiFilter("all");
                      }}
                      className={`border-0 px-3 py-1 ${activeView === "weekly" ? "active" : ""}`}
                    >
                      {t("attendanceOverview.weekly", "Weekly")}
                    </button>
                    {/* <button
                      type="button"
                      onClick={() => {
                        setActiveView("monthly");
                        setActiveKpiFilter("all");
                      }}
                      className={`border-0 px-3 py-1 ${activeView === "monthly" ? "active" : ""}`}
                    >
                      {t("attendanceOverview.monthly", "Monthly")}
                    </button> */}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

        <Row className="g-3 mb-3">
          {kpiTiles.map(({ key, label, color, badgeColor, icon, count, actionLabel, subtext }) => (
            <Col xs={12} sm={6} lg={4} xl className="col-xxl" key={key}>
              <Card
                className={`absence-kpi-card absence-kpi-border-${color} h-100 flex-grow-0 rounded-3 card-hover border cursor-pointer ${
                  activeKpiFilter === key ? `absence-kpi-active absence-kpi-selected-${color}` : "shadow-none"
                }`}
                role="button"
                tabIndex={0}
                onClick={() => setActiveKpiFilter((prev) => (prev === key ? "all" : key))}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setActiveKpiFilter((prev) => (prev === key ? "all" : key));
                  }
                }}
              >
                <Card.Body className="py-3 px-3">
                  <div className="mb-2">
                    <div className={`d-flex align-items-center justify-content-center rounded-2 stat-icon bg-${badgeColor}-subtle`}>
                      <FontAwesomeIcon icon={icon} className={`fs-8 text-${color}`} />
                    </div>
                  </div>

                  <div className="mb-1 fw-bolder font-size-22">{count}</div>

                  <span className="mb-1 fw-semibold text-900 font-size-13 d-block">{label}</span>

                  <div className="d-flex justify-content-between align-items-center gap-2">
                    <div className="font-size-12 text-600 fw-normal">{activeView === "daily" ? subtext : periodSubtext}</div>
                    <div className={`p-0 d-inline-block text-decoration-none fw-medium font-size-12 text-${color} text-nowrap`}>
                      {actionLabel}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

      <Card className="mb-3">
          <Card.Header>
          <Row className="align-items-center justify-content-between g-2">
            <Col xs="auto" className="d-flex align-items-center">
              <AdvanceTableSearchBox setGlobalFilter={setSearchInput} />
            </Col>

            <Col xs="auto" className="calendar-title-center d-flex align-items-center justify-content-center">
              <div className="d-flex align-items-center gap-1">
                  <Button
                    variant="link"
                    className="icon-item icon-item-sm shadow-none p-0"
                    onClick={() => handleDateShift(-1)}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} />
                  </Button>

                  <div className="dateRange-custom-width position-relative">
                    <DatePicker
                      selected={selectedDate}
                      onChange={(date) => {
                        if (date) setSelectedDate(date);
                        setIsDatePickerOpen(false);
                      }}
                      open={isDatePickerOpen}
                      onInputClick={() => setIsDatePickerOpen(true)}
                      onClickOutside={() => setIsDatePickerOpen(false)}
                      className="form-control form-control-sm w-100 text-center px-1"
                      dateFormat="dd-MM-yyyy"
                      popperPlacement="bottom-start"
                      value={dateDisplayValue}
                    />
                  </div>

                  {/* <DropdownFilter
                    filters={monthFilters}
                    currentFilter={selectedMonth}
                    handleFilter={setSelectedMonth}
                    icon="calendar-alt"
                    right
                  /> */}
                  <Button
                    variant="link"
                    className="icon-item icon-item-sm shadow-none p-0"
                    onClick={() => handleDateShift(1)}
                  >
                    <FontAwesomeIcon icon={faArrowRight} />
                  </Button>
              </div>
            </Col>

             <Col xs="auto" className="d-flex align-items-center gap-2">
               {activeKpiFilter !== "all" && (
                  <span className="badge rounded-pill fw-medium font-size-11 badge-subtle-primary d-inline-flex align-items-center gap-2 px-2 py-1 text-nowrap">
                    {`${t("filtered", "Filtered")}: ${t(KPI_LABELS[activeKpiFilter])}`}
                    <button
                      type="button"
                      className="btn-close p-0 filter-chip-close"
                      aria-label={t("clear", "Clear")}
                      onClick={() => setActiveKpiFilter("all")}
                    />
                  </span>
                )}
              <Dropdown align="end">
                <Dropdown.Toggle
                  variant="falcon-default"
                  size="sm"
                  className="d-flex align-items-center shadow-none border-300 py-1"
                  aria-label={t("exportLabel" , "Export")}
                  title={t("exportLabel" , "Export")}
                  disabled={isExporting}
                >
                  <span>{t("exportLabel", "Export")}</span>
                </Dropdown.Toggle>
                <Dropdown.Menu className="export-dropdown-menu">
                  <Dropdown.Item onClick={() => handleExport("excel")}>
                    <FontAwesomeIcon icon={faFileExcel} className="text-success me-2" />
                    {t("exportexcel")}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleExport("csv")}>
                    <FontAwesomeIcon icon={faFileCsv} className="text-primary me-2" />
                    {t("exportcsv")}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleExport("pdf")}>
                    <FontAwesomeIcon icon={faFilePdf} className="text-danger me-2" />
                    {t("exportpdf")}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              </Col>
            </Row>
          </Card.Header>

        <Card.Body className="p-3">
          {stateBlock}
          {!stateBlock && activeView === "daily" && (
            <div className="table-responsive scrollbar">
              <table className="table table-sm table-hover mb-0 overflow-hidden fs-10 data-font-size align-middle text-start">
                <thead className="bg-200 text-nowrap align-middle text-start">
                  <tr>
                    <th>{t("employee", "Employee")}</th>
                    <th>{t("status", "Status")}</th>
                    <th>{t("clockIn", "Clock-in")}</th>
                    <th>{t("clockOut", "Clock-out")}</th>
                    <th>{t("registeredHours", "Registered hours")}</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyRows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-4 text-muted">
                        {t("noDataAvailable", "No employees match these filters.")}
                      </td>
                    </tr>
                  ) : (
                      dailyRows.map((row) => (
                        <tr 
                          key={row.id} 
                          className="align-middle text-start cursor-pointer" 
                          onClick={() => handleRowClick(row)}
                        >
                        <td>
                          <div className="d-flex align-items-center text-start">
                            <div
                              className={`avatar avatar-xl rounded-circle ${row.avatarClass} d-flex align-items-center justify-content-center me-3`}
                            >
                              <span className="fw-bold fs-9">{row.initials}</span>
                            </div>
                            <div>
                              <div className="fw-semibold">{row.name}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <SubtleBadge
                            pill
                            bg={row.dayData.isLive ? "primary" : row.statusMeta.badgeColor}
                            className={!row.dayData.isLive && row.statusMeta.dashed ? "badge-dashed" : ""}
                          >
                            <p className="mb-0 data-font-size">
                              {row.dayData.isLive
                                ? t("attendanceOverview.inProgress", "In progress")
                                : t(row.statusMeta.labelKey, row.statusMeta.label)}
                            </p>
                          </SubtleBadge>
                        </td>
                        <td className="text-700">{row.dayData.checkIn || "—"}</td>
                        <td className="text-700">{row.dayData.checkOut || "—"}</td>
                        <td className="text-700">
                          {row.dayData.formattedHours ? (
                            row.dayData.formattedHours
                          ) : row.dayData.isLive ? (
                              <SubtleBadge pill bg="primary">
                                <p className="mb-0 data-font-size">
                                  {t("attendanceOverview.inProgress", "In progress")}
                                </p>
                              </SubtleBadge>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {!stateBlock && activeView === "weekly" && (
            <div className="table-responsive scrollbar">
              <table className="table table-sm mb-0 overflow-hidden fs-10 data-font-size align-middle text-center attendance-week-table">
                <colgroup>
                  <col className="attendance-week-employee-col" />
                  {weekDays.map((d) => (
                    <col key={d.dateStr} className="attendance-week-day-col" />
                  ))}
                </colgroup>
                <thead className="bg-200 text-nowrap align-middle">
                  <tr>
                    <th className="text-start">{t("employee", "Employee")}</th>
                    {weekDays.map((d) => (
                      <th key={d.dateStr}>
                        <div>{d.dayName}</div>
                        <div className="fs-11 text-700 fw-normal">{d.dayMonth}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {weeklyRows.map((row) => (
                    <tr key={row.id} className="align-middle cursor-pointer">
                      <td
                        className="text-start"
                        onClick={() =>
                          handleRowClick(
                            row,
                            weekDays.some((w) => w.dateStr === selectedDay) ? selectedDay : weekDays[0].dateStr
                          )
                        }
                      >
                        <div className="d-flex align-items-center text-start">
                          <div
                            className={`avatar avatar-xl rounded-circle ${row.avatarClass} d-flex align-items-center justify-content-center me-3`}
                          >
                            <span className="fw-bold fs-9">{row.initials}</span>
                          </div>
                          <div>
                            <div className="fw-semibold">{row.name}</div>
                          </div>
                        </div>
                      </td>
                      {row.days.map((d) => (
                        <td key={d.dateStr} onClick={() => handleRowClick(row, d.dateStr)}>
                          {d.dayData.isFuture ? (
                            <span className="text-500">-</span>
                          ) : (
                            <div>
                              <SubtleBadge
                                pill
                                bg={d.dayData.isLive ? "primary" : d.statusMeta.badgeColor}
                                className={!d.dayData.isLive && d.statusMeta.dashed ? "badge-dashed" : ""}
                              >
                                <p className="mb-0 data-font-size">
                                  {d.dayData.isLive
                                    ? t("attendanceOverview.inProgress", "In progress")
                                    : t(d.statusMeta.labelKey, d.statusMeta.label)}
                                </p>
                              </SubtleBadge>
                              {d.dayData.formattedHours && (
                                <div className="fs-11 text-900 fw-medium mt-1">{d.dayData.formattedHours}</div>
                              )}
                            </div>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* {activeView === "monthly" && (
            <>
              <div className="table-responsive attendance-month-scroll">
                <table className="table table-hover mb-0 overflow-hidden data-font-size align-middle text-center">
                  <thead className="bg-200 text-nowrap align-middle sticky-top z-1">
                    <tr>
                      <th className="py-2 px-3 text-start bg-200 border-end min-w-200 position-sticky start-0 z-3">
                      {t("employee", "Employee")}
                      </th>
                      {monthDays.map((d) => (
                        <th
                          key={d.dayNum}
                          className={`py-1 px-1 min-w-32 ${d.isWeekend ? "text-400 bg-100" : ""}`}
                        >
                          <div>{d.dayNum}</div>
                          <div className="fs-11 text-500 fw-normal">{d.dayInitial}</div>
                        </th>
                      ))}
                      <th className="py-2 px-2 bg-success-subtle text-success border-start min-w-80">
                        Days worked
                      </th>
                      <th className="py-2 px-2 bg-success-subtle text-success min-w-80">
                        Hours worked
                      </th>
                      <th className="py-2 px-2 bg-primary-subtle text-primary min-w-75">
                        Leave days
                      </th>
                      <th className="py-2 px-2 bg-danger-subtle text-danger min-w-75">
                        Sick days
                      </th>
                      <th className="py-2 px-2 bg-danger-subtle text-danger min-w-75">
                        Absences
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleMonthlyRows.map((row) => (
                      <tr key={row.id} className="cursor-pointer" onClick={() => handleRowClick(row)}>
                        <td className="py-2 px-3 text-start bg-white border-end position-sticky start-0 z-2">
                          <div className="d-flex align-items-center text-start">
                            <div
                              className={`avatar avatar-l rounded-circle ${row.avatarClass} d-flex align-items-center justify-content-center me-2`}
                            >
                              <span className="fw-bold fs-10">{row.initials}</span>
                            </div>
                            <div>
                              <div className="fw-semibold">{row.name}</div>
                              <div className="fs-11 text-500">{row.location} · {row.team}</div>
                            </div>
                          </div>
                        </td>
                        {row.days.map((d) => (
                          <td key={d.dayNum} className={`p-1 ${d.isWeekend ? "bg-100" : ""}`}>
                            {d.dayData.isFuture ? null : (
                              <div
                                className={`d-flex align-items-center justify-content-center mx-auto rounded ${d.statusMeta.subtleClass} fw-semibold attendance-day-cell font-size-11`}
                                title={`${d.statusMeta.label} · Day ${d.dayNum}`}
                              >
                                {d.dayData.s === "N" ? "–" : d.dayData.s}
                              </div>
                            )}
                          </td>
                        ))}
                        <td className="fw-bold text-success border-start">{row.totals.daysWorked}</td>
                        <td className="fw-bold text-success">{row.totals.hoursWorkedDisplay} h</td>
                        <td className="fw-bold text-primary">{row.totals.leaveDays}</td>
                        <td className="fw-bold text-danger">{row.totals.sickDays}</td>
                        <td className="fw-bold text-danger">{row.totals.absences}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="px-3 py-2 border-top fs-11 text-500 bg-light d-flex justify-content-between">
                <div>
                  Month: <strong>{monthMeta.monthName} {monthMeta.year}</strong> ({monthMeta.daysCount} days) ·{" "}
                  <strong>{visibleMonthlyRows.length}</strong> employees
                </div>
                <div className="text-primary fw-medium">Sum of daily cells = Total columns</div>
              </div>
            </>
          )} */}
        </Card.Body>
        {!stateBlock && totalCount > 0 && (
          <Card.Footer>
            <AdvanceTableProvider {...attendanceTable}>
              <ManualPaginationFooter
                totalCount={totalCount}
                pageIndex={pageIndex}
                pageSize={pageSize}
                tableKey="attendanceTable"
                onPageSizeChange={(newSize) => {
                  setPageSize(newSize);
                  setPageIndex(0);
                }}
              />
            </AdvanceTableProvider>
          </Card.Footer>
        )}
      </Card>

      <AttendanceSidePanel
        show={isDrawerOpen}
        onHide={() => setIsDrawerOpen(false)}
        employee={selectedDrawerEmployee}
        date={drawerDate}
        history={drawerHistory}
        isHistoryLoading={isDrawerHistoryLoading}
        hasHistoryError={hasDrawerHistoryError}
      />
    </>
  );
};

export default Attendance;